import React, { useEffect, useState } from 'react';
import { MapPin, Calendar, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { settingsAPI } from '../utils/api';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => {
    settingsAPI.get().then((res) => setS(res.data)).catch(() => setS({}));
  }, []);

  const v = (key, fallback) => (s && s[key]) ? s[key] : fallback;

  return (
    <div className="min-h-screen">
      <div 
        className="relative py-20"
      >
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Jak to funguje</h1>
          <p className="text-xl text-white/90">
            Jednoduché kroky k vašemu divokému campingovému dobrodružství
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20">
        {/* For Campers */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12 text-white drop-shadow-lg">Pro kempaře</h2>
          
          <div className="space-y-12">
            <StepCard
              number="1"
              title={v('hiw_step1_title', 'Najdi své ideální místo')}
              description={v('hiw_step1_text', 'Použijte naši vyhledávací funkci k nalezení perfektního místa pro vaše potřeby.')}
              icon={<MapPin className="w-12 h-12" />}
              image="https://images.unsplash.com/photo-1686224825725-0864c5bca12b"
            />

            <StepCard
              number="2"
              title={v('hiw_step2_title', 'Rezervuj termín')}
              description={v('hiw_step2_text', 'Vyberte si datum příjezdu a odjezdu, zadejte počet hostů a dokončete rezervaci.')}
              icon={<Calendar className="w-12 h-12" />}
              image="https://images.pexels.com/photos/12913674/pexels-photo-12913674.jpeg"
              reverse
            />

            <StepCard
              number="3"
              title={v('hiw_step3_title', 'Vyraž do přírody')}
              description={v('hiw_step3_text', 'Připravte si vybavení a vydejte se na své dobrodružství.')}
              icon={<Users className="w-12 h-12" />}
              image="https://images.pexels.com/photos/16778094/pexels-photo-16778094.jpeg"
            />
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/locations')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-6 text-lg font-semibold h-auto"
            >
              Začít hledat místa
            </Button>
          </div>
        </section>

        {/* For Landowners */}
        <section className="bg-white rounded-2xl p-12 shadow-lg">
          <h2 className="text-3xl font-bold text-center mb-12">Pro majitele pozemků</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <BenefitCard
              icon={<Clock className="w-10 h-10" />}
              title="Snadné nastavení"
              description="Přidejte své místo během několika minut. Stačí vyplnit základní informace a nahrát fotografie."
            />
            <BenefitCard
              icon={<Calendar className="w-10 h-10" />}
              title="Kontrola dostupnosti"
              description="Plná kontrola nad tím, kdy je vaše místo dostupné. Nastavte vlastní ceny a pravidla."
            />
            <BenefitCard
              icon={<Users className="w-10 h-10" />}
              title="Komunita kempařů"
              description="Spojte se s tisíci kempařů hledajících autentické outdoorové zážitky."
            />
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/add-location')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-6 text-lg font-semibold h-auto"
            >
              Přidat své místo
            </Button>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-12 text-white drop-shadow-lg">Často kladené otázky</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <FAQItem
              question={v('hiw_faq_q1', 'Je divoké kempování legální?')}
              answer={v('hiw_faq_a1', 'Divoké kempování podléhá místním zákonům.')}
            />
            <FAQItem
              question={v('hiw_faq_q2', 'Co mám vzít s sebou?')}
              answer={v('hiw_faq_a2', 'Základní vybavení zahrnuje stan, spacák, jídlo a vodu.')}
            />
            <FAQItem
              question={v('hiw_faq_q3', 'Mohu zrušit rezervaci?')}
              answer={v('hiw_faq_a3', 'Ano, podle storno politiky.')}
            />
            <FAQItem
              question={v('hiw_faq_q4', 'Jak funguje platba?')}
              answer={v('hiw_faq_a4', 'Platba probíhá online při vytvoření rezervace.')}
            />
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

const StepCard = ({ number, title, description, icon, image, reverse = false }) => (
  <Card className="overflow-hidden shadow-lg">
    <CardContent className="p-0">
      <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8`}>
        <div className="md:w-1/2">
          <img
            src={image}
            alt={title}
            className="w-full h-64 md:h-full object-cover"
          />
        </div>
        <div className="md:w-1/2 p-8 flex flex-col justify-center">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {number}
            </div>
            <div className="text-orange-600">
              {icon}
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-4">{title}</h3>
          <p className="text-gray-700 leading-relaxed">{description}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const BenefitCard = ({ icon, title, description }) => (
  <Card className="text-center hover:shadow-lg transition-shadow">
    <CardContent className="p-6">
      <div className="text-orange-600 flex justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </CardContent>
  </Card>
);

const FAQItem = ({ question, answer }) => (
  <Card>
    <CardContent className="p-6">
      <h3 className="text-lg font-bold mb-2">{question}</h3>
      <p className="text-gray-700">{answer}</p>
    </CardContent>
  </Card>
);

export default HowItWorks;
